import {
  MenuItemType,
  SortBy,
  SortSectionItem,
  SpecialActions,
  SpecialControl,
  StagedItemType,
} from 'common/query-builder/query-block/properties-menu/types';
import {DATASET_PROPERTY_NAME, PropertyType, ResourceType} from 'common/report/constants';
import {Placement} from 'common/components/tooltip/types';
import {
  OrderedStandardTimeUnits,
  SpecialTimeUnit,
  TimeUnit,
  TimeUnitLabels,
} from 'common/widgets/date-range-picker/models/types';
import {pluralize} from 'common/util/string';

import {PropertySectionItem, SectionItemProperty} from './item-menu-types';

export const EXTRA_PROPERTY_DEFINITIONS_BY_TYPE = {
  [ResourceType.Event]: [
    {
      name: `$dataset`,
      displayName: `Project Name`,
      description: `Project Name is a virtual property that you can use to breakdown or filter your data based on the Project ID in which this event/property is tracked. [More about Rollup Projects](https://mixpanel.app.box.com/v/rollup-projects-beta)`,
      type: PropertyType.String,
      resourceType: ResourceType.Event,
    },
  ],
  [ResourceType.User]: [
    {
      name: `$mp_first_event_time`,
      displayName: `First Seen`,
      description: `First Seen is a datetime profile property that captures the timestamp of the very first event for this user/group. This can be used to filter/segment reports by users who were "new" in a particular timeframe. [More about First Seen](https://docs.mixpanel.com/docs/analysis/advanced/other-advanced-features#analyzing-first-time-users)`,
      type: PropertyType.Datetime,
      resourceType: ResourceType.User,
    },
  ],
};

export const TOP_PEOPLE: PropertySectionItem = {
  type: null,
  propertyDefaultType: null,
  hasCaret: false,
  hasPill: false,
  icon: `profile`,
  isDisabled: false,
  label: `All User Profiles`,
  value: `$all_people`,
  resourceType: ResourceType.User,
  menuItemType: MenuItemType.UserProperties,
};

export const TOP_PATHS: PropertySectionItem = {
  type: PropertyType.NonProperty,
  propertyDefaultType: PropertyType.NonProperty,
  hasCaret: false,
  hasPill: false,
  icon: `top-paths`,
  isDisabled: false,
  label: `Top paths`,
  value: `$paths`,
  resourceType: ResourceType.Other,
  menuItemType: MenuItemType.Other,
};

export const BEHAVIOR_FREQUENCY: PropertySectionItem = {
  type: PropertyType.Number,
  propertyDefaultType: PropertyType.Number,
  caretStyle: `caret`,
  hasCaret: true,
  hasPill: false,
  icon: `type-custom-number`,
  isDisabled: false,
  label: `Frequency per User...`,
  dynamicLabel: ({groupTitle}) => `Frequency per ${groupTitle}...`,
  value: `$frequency`,
  resourceType: ResourceType.Other,
  tooltipData: {
    header: `Frequency per User...`,
    description: `A behavioral property that tracks the frequency of an event per user. This type of behavioral property is useful for understanding how often users did an action and correlating it to other actions. [Learn more in our help docs](https://docs.mixpanel.com/docs/analysis/advanced/other-advanced-features#behavioral-properties)`,
    entityIcon: `type-custom-number`,
    hideMetricsSection: true,
  },
  menuItemType: MenuItemType.BehaviorWithEvent,
};

export const BEHAVIOR_AGGREGATION: PropertySectionItem = {
  type: PropertyType.Number,
  propertyDefaultType: PropertyType.Number,
  caretStyle: `caret`,
  hasCaret: true,
  hasPill: false,
  icon: `type-custom-number`,
  isDisabled: false,
  label: `Aggregate Property per User...`,
  dynamicLabel: ({groupTitle}) => `Aggregate Property per ${groupTitle}...`,
  value: `$aggregation`,
  resourceType: ResourceType.Other,
  tooltipData: {
    header: `Aggregate Property per User...`,
    description: `A behavioral property that tracks the property aggregation of an event per user. This type of behavioral property is useful for understanding the value of an action and correlating it to other actions. [Learn more in our help docs](https://docs.mixpanel.com/docs/analysis/advanced/other-advanced-features#behavioral-properties)`,
    entityIcon: `type-custom-number`,
    hideMetricsSection: true,
  },
  menuItemType: MenuItemType.BehaviorWithEvent,
};

export const BEHAVIOR_MULTI_ATTRIBUTION: PropertySectionItem = {
  type: PropertyType.String,
  propertyDefaultType: PropertyType.String,
  caretStyle: `caret`,
  hasCaret: true,
  hasPill: false,
  icon: `type-custom-text`,
  isDisabled: false,
  label: `Attributed by...`,
  value: `$multi_attribution`,
  resourceType: ResourceType.Event,
  tooltipData: {
    header: `Attributed by...`,
    description: `Special property that associates touchpoints to the conversion metric. This type of segmentation is useful for understanding which touchpoints impacted conversion ultimately. [Learn More](https://docs.mixpanel.com/docs/analysis/advanced/attribution)`,
    entityIcon: `type-custom-text`,
    hideMetricsSection: true,
    // Can flip this to true when we're ready to have the badge displayed post GA
    showNewForFreeUsers: false,
    noteForFreeUsers: `Limited models for Free plans`,
    noteForPaidUsers: `Available for a limited time at no cost`,
  },
  menuItemType: MenuItemType.BehaviorMultiAttribution,
  stagedItemType: StagedItemType.BehaviorMultiAttribution,
};

export const FUNNELS_TIME_TO_CONVERSION: PropertySectionItem = {
  type: PropertyType.Number,
  propertyDefaultType: PropertyType.Number,
  hasCaret: false,
  hasPill: false,
  icon: `type-custom-number`,
  isDisabled: false,
  label: `Time to Convert`,
  value: `$time_to_conversion`,
  resourceType: ResourceType.Other,
  tooltipData: {
    header: `Time to Convert`,
    description: `The time in seconds from Step 1 to the conversion event.`,
    entityIcon: `type-custom-number`,
    extraInfo: [],
    hideMetricsSection: true,
  },
  menuItemType: MenuItemType.Other,
};

export const FLOWS_CONVERSION: PropertySectionItem = {
  type: PropertyType.String,
  propertyDefaultType: PropertyType.String,
  hasCaret: false,
  hasPill: false,
  icon: `top-paths`,
  isDisabled: false,
  label: `Conversion`,
  value: `$conversion`,
  resourceType: ResourceType.Other,
  tooltipData: {
    header: `Conversion`,
    description: `Find which events are more likely to lead to conversion`,
    entityIcon: `top-paths`,
    extraInfo: [`The flow report must have atleast two steps to do conversion analysis`],
    hideMetricsSection: true,
  },
  menuItemType: MenuItemType.Other,
};

export const FLOWS_CONVERSION_VALUES: string[] = [`Converted`, `Did Not Convert`];

export const ALL_USERS_COHORT: PropertySectionItem = {
  type: null,
  propertyDefaultType: null,
  hasCaret: false,
  hasPill: false,
  isDisabled: false,
  label: `All Users`,
  value: `$all_users`,
  resourceType: ResourceType.Cohort,
  menuItemType: MenuItemType.Cohorts,
};

export const FIRST_EVENT_TIME_PROPERTY: PropertySectionItem = {
  label: `First Seen`,
  icon: `calendar`,
  value: `$mp_first_event_time`,
  propertyDefaultType: PropertyType.Datetime,
  type: PropertyType.Datetime,
  resourceType: ResourceType.User,
  isControlItem: false,
  menuItemType: MenuItemType.UserProperties,
};

export const DATASET_PROPERTY: PropertySectionItem = {
  label: `Project Name`,
  icon: `star-top-events`,
  value: DATASET_PROPERTY_NAME,
  propertyDefaultType: PropertyType.String,
  type: PropertyType.String,
  resourceType: ResourceType.Event,
  menuItemType: MenuItemType.EventProperties,
};

export const BEHAVIOR_PROPERTIES: Map<string, PropertySectionItem> = new Map([
  [BEHAVIOR_AGGREGATION.value, BEHAVIOR_AGGREGATION],
  [BEHAVIOR_FREQUENCY.value, BEHAVIOR_FREQUENCY],
  [BEHAVIOR_MULTI_ATTRIBUTION.value, BEHAVIOR_MULTI_ATTRIBUTION],
]);

// These properties will have forced order when sorted by relevancy (0 indexed)
// TODO remove this when we work on the new properties menu
export const SPECIAL_ORDER_PROPERTIES: Map<string, number> = new Map([
  [FLOWS_CONVERSION.value, 0],
  [TOP_PATHS.value, 0],
  [FUNNELS_TIME_TO_CONVERSION.value, 0],
  [BEHAVIOR_FREQUENCY.value, 3],
  [BEHAVIOR_AGGREGATION.value, 4],
  [BEHAVIOR_MULTI_ATTRIBUTION.value, 3],
]);

export const SPECIAL_PROPERTIES: Map<string, PropertySectionItem> = new Map([
  [TOP_PEOPLE.value, TOP_PEOPLE],
  [TOP_PATHS.value, TOP_PATHS],
  [FLOWS_CONVERSION.value, FLOWS_CONVERSION],
  [FUNNELS_TIME_TO_CONVERSION.value, FUNNELS_TIME_TO_CONVERSION],
  [BEHAVIOR_AGGREGATION.value, BEHAVIOR_AGGREGATION],
  [BEHAVIOR_FREQUENCY.value, BEHAVIOR_FREQUENCY],
  [BEHAVIOR_MULTI_ATTRIBUTION.value, BEHAVIOR_MULTI_ATTRIBUTION],
  [ALL_USERS_COHORT.value, ALL_USERS_COHORT],
  [FIRST_EVENT_TIME_PROPERTY.value, FIRST_EVENT_TIME_PROPERTY],
  [DATASET_PROPERTY.value, DATASET_PROPERTY],
]);

export const OTHER_OPTIONS: Map<string, PropertySectionItem> = new Map([
  [TOP_PATHS.value, TOP_PATHS],
  [FLOWS_CONVERSION.value, FLOWS_CONVERSION],
  [FUNNELS_TIME_TO_CONVERSION.value, FUNNELS_TIME_TO_CONVERSION],
  [BEHAVIOR_FREQUENCY.value, BEHAVIOR_FREQUENCY],
  [BEHAVIOR_AGGREGATION.value, BEHAVIOR_AGGREGATION],
  [BEHAVIOR_MULTI_ATTRIBUTION.value, BEHAVIOR_MULTI_ATTRIBUTION],
]);

export const SPECIAL_CONTROLS: Record<string, SpecialControl> = {
  CREATE_INLINE_COHORT: {
    isControlItem: true,
    type: null,
    propertyDefaultType: null,
    icon: `compose`,
    label: `Create Cohort...`,
    resourceType: ResourceType.Cohort,
    value: SpecialActions.CreateInlineCohort,
  },
  CREATE_CUSTOM_EVENT_FORMULA: {
    icon: `compose`,
    isControlItem: true,
    type: null,
    propertyDefaultType: null,
    label: `Create a Custom Property...`,
    resourceType: ResourceType.Event,
    value: SpecialActions.CreateCustomEventFormula,
    tooltipData: {
      header: `Create a Custom Property`,
      description: `• Merge and regroup property values\n• Resize numeric ranges\n• Extract patterns`,
      entityIcon: `compose`,
      helpLink: {
        label: `More on custom properties`,
        href: `https://docs.mixpanel.com/docs/analysis/advanced/custom-properties`,
      },
    },
  },
  CREATE_CUSTOM_PEOPLE_FORMULA: {
    icon: `compose`,
    isControlItem: true,
    type: null,
    propertyDefaultType: null,
    label: `Create a Custom Property...`,
    resourceType: ResourceType.User,
    value: SpecialActions.CreateCustomPeopleFormula,
    tooltipData: {
      header: `Create a Custom Property`,
      description: `• Merge and regroup property values\n• Resize numeric ranges\n• Extract patterns`,
      entityIcon: `compose`,
      helpLink: {
        label: `More on custom properties`,
        href: `https://docs.mixpanel.com/docs/analysis/advanced/custom-properties`,
      },
    },
  },
  CREATE_CUSTOM_BEHAVIORAL_PROPERTY: {
    icon: `compose`,
    isControlItem: true,
    type: null,
    propertyDefaultType: null,
    label: `Create a Behavior...`,
    resourceType: ResourceType.User,
    value: SpecialActions.CreateCustomBehavioralProperties,
    tooltipData: {
      header: `Create a Behavior`,
      description: `Define properties based on user behavior patterns over time. Common use-cases include: number of times users did an action, summarize user's actions based on a property.`,
      entityIcon: `compose`,
    },
  },
};

export const DATETIME_UNIT_OPTIONS = [
  ...OrderedStandardTimeUnits.filter((unit) => unit !== TimeUnit.Minute),
  ...Object.values(SpecialTimeUnit),
];

export const DATETIME_UNIT_OPTION_SECTION_ITEMS = DATETIME_UNIT_OPTIONS.map((unitValue) => ({
  label: TimeUnitLabels[unitValue],
  value: unitValue,
}));

export const DURATION_UNIT_OPTIONS = [...OrderedStandardTimeUnits];

export const DURATION_UNIT_OPTION_SECTION_ITEMS = DURATION_UNIT_OPTIONS.map((unitValue) => ({
  label: pluralize(TimeUnitLabels[unitValue], true),
  value: unitValue,
}));

export const SORT_ON_RELEVANCE: SortSectionItem = {
  label: `Relevancy`,
  icon: `relevancy`,
  value: SortBy.Relevance,
  tooltip: {
    placement: Placement.Right,
    text: `Sorting based on recency and frequency of use in your project`,
    'max-width': `160px`,
  },
};

export const SORT_ON_ALPHA_ASCENDING: SortSectionItem = {
  label: `Name A-Z`,
  icon: `sort-alpha-asc`,
  value: SortBy.NameAsc,
  tooltip: {
    placement: Placement.Right,
    text: `Sorting based on event name starting with A`,
    'max-width': `160px`,
  },
};

export const SORT_ON_ALPHA_DESCENDING: SortSectionItem = {
  label: `Name Z-A`,
  icon: `sort-alpha-desc`,
  value: SortBy.NameDesc,
  tooltip: {
    placement: Placement.Right,
    text: `Sorting based on event name starting with Z`,
    'max-width': `160px`,
  },
};

export const SORT_OPTION_ITEMS: SortSectionItem[] = [
  SORT_ON_RELEVANCE,
  SORT_ON_ALPHA_ASCENDING,
  SORT_ON_ALPHA_DESCENDING,
];

export const AllFilter = {label: `all`, value: {value: `all`}};

export const LIST_PROPERTY_SUM: SectionItemProperty = {
  type: PropertyType.Number,
  propertyDefaultType: PropertyType.Number,
  caretStyle: `caret`,
  persistCaret: true,
  hasCaret: true,
  hasPill: false,
  icon: `type-custom-number`,
  isDisabled: false,
  label: `Sum`,
  value: `$list_sum`,
  resourceType: ResourceType.Other,
  tooltipData: {
    header: `Sum`,
    description: `The total value for a property across all of the items in the list. [Learn More](https://docs.mixpanel.com/docs/analysis/advanced/other-advanced-features#computed-properties)`,
    entityIcon: `type-custom-number`,
    hideMetricsSection: true,
  },
  menuItemType: MenuItemType.ComputedListPropertySum,
  stagedItemType: StagedItemType.ComputedListNumericProperty,
};

export const LIST_PROPERTY_UNIQUES: SectionItemProperty = {
  type: PropertyType.Number,
  propertyDefaultType: PropertyType.Number,
  caretStyle: `caret`,
  persistCaret: true,
  hasCaret: true,
  hasPill: false,
  icon: `type-custom-number`,
  isDisabled: false,
  label: `Distinct Count`,
  value: `$list_unique`,
  resourceType: ResourceType.Other,
  tooltipData: {
    header: `Distinct Count`,
    description: `The number of unique values for a property across all of the items in the list. [Learn More](https://docs.mixpanel.com/docs/analysis/advanced/other-advanced-features#computed-properties)`,
    entityIcon: `type-custom-number`,
    hideMetricsSection: true,
  },
  menuItemType: MenuItemType.ComputedListPropertyUniques,
  stagedItemType: StagedItemType.ComputedListProperty,
};

export const LIST_LENGTH: SectionItemProperty = {
  type: PropertyType.Number,
  propertyDefaultType: PropertyType.Number,
  icon: `type-custom-number`,
  isDisabled: false,
  label: `Size`,
  value: `$list_length`,
  resourceType: ResourceType.Other,
  tooltipData: {
    header: `Size`,
    description: `The number of items in the list. [Learn More](https://docs.mixpanel.com/docs/analysis/advanced/other-advanced-features#computed-properties)`,
    entityIcon: `type-custom-number`,
    hideMetricsSection: true,
  },
  menuItemType: MenuItemType.ComputedListLength,
};

export const COMPUTED_LIST_PROPERTIES_VALUES = new Set([
  LIST_LENGTH.value,
  LIST_PROPERTY_SUM.value,
  LIST_PROPERTY_UNIQUES.value,
]);

export const COMPUTED_LIST_PROPERTIES_LABELS = {
  [LIST_LENGTH.value]: `Size`,
  [LIST_PROPERTY_SUM.value]: `Sum`,
  [LIST_PROPERTY_UNIQUES.value]: `Distinct Count`,
};
